import React, { useState } from "react";
import { pdf } from "@react-pdf/renderer";
import { useRequest } from "./useRequest";
import { buildDesignPages } from "../utils/pdfUtils";
import { DataProps, PullingDesignProps } from "../interfaces/interfaces";
import { PdfProposalFile } from "../components/pdf/files/PdfProposalFile";
import { PdfTallyFile } from "../components/pdf/files/PdfTallyFile";
import { PdfPullingReport } from "../components/pdf/files/PdfPullingReport";
import {
  initial_sales_state,
  initial_solution_state,
  initial_simulator_state,
} from "../utils/data";

function triggerDownload(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute(
    "download",
    filename.endsWith(".pdf") ? filename : `${filename}.pdf`,
  );
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export const useAppDesignPdf = () => {
  const { handleRequest } = useRequest();
  const [isGenerating, setIsGenerating] = useState(false);

  const downloadProposalPdf = async (id: number, filename: string) => {
    setIsGenerating(true);
    try {
      const options: RequestInit = { method: "GET" };
      const response = await handleRequest({
        endpoint: `proposal/${id}`,
        options,
      });
      if (!response.success) throw new Error("Failed to fetch proposal");
      const record = response.data;
      const data: DataProps = {
        basicInfo: record.basicInfo,
        salesInfo: initial_sales_state,
        solution: record.solution || initial_solution_state,
        simulator: record.simulator || initial_simulator_state,
        tallyDesign: record.tallyDesign || [],
        wellbore3dImg: record.wellboreImage || null,
        proposalDescription: record.proposalDescription || null,
        wbdDesign: record.wbdDesign || [],
        designByPage: buildDesignPages(
          record.wbdDesign || [],
          record.tallyDesign || [],
          record.basicInfo?.bhaInfo?.tol,
        ),
      };
      const blob = await pdf(
        React.createElement(PdfProposalFile, { data }) as any,
      ).toBlob();
      triggerDownload(blob, filename);
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadTallyPdf = async (id: number, filename: string) => {
    setIsGenerating(true);
    try {
      const options: RequestInit = { method: "GET" };
      const response = await handleRequest({ endpoint: `tally/${id}`, options });
      if (!response.success) throw new Error("Failed to fetch tally");
      const record = response.data;
      const data: DataProps = {
        basicInfo: record.basicInfo,
        salesInfo: initial_sales_state,
        solution: initial_solution_state,
        simulator: initial_simulator_state,
        tallyDesign: record.tallyDesign || [],
        wellbore3dImg: null,
        proposalDescription: null,
        wbdDesign: record.wbdDesign || [],
        designByPage: buildDesignPages(
          record.wbdDesign || [],
          record.tallyDesign || [],
          record.basicInfo?.bhaInfo?.tol,
        ),
      };
      const blob = await pdf(
        React.createElement(PdfTallyFile, { data }) as any,
      ).toBlob();
      triggerDownload(blob, filename);
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadPullingPdf = async (id: number, filename: string) => {
    setIsGenerating(true);
    try {
      const options: RequestInit = { method: "GET" };
      const response = await handleRequest({
        endpoint: `pulling/${id}`,
        options,
      });
      if (!response.success) throw new Error("Failed to fetch pulling design");
      const record = response.data;
      const pullingInfo: PullingDesignProps = {
        id: record.id,
        customName: record.customName,
        pullingName: record.pullingName,
        well: record.well,
        installationDate: record.installationDate,
        pullingDate: record.pullingDate,
        runtime: record.runtime || 0,
        details: record.details || [],
        recomendations: record.recomendations || [],
      };
      const blob = await pdf(
        React.createElement(PdfPullingReport, { pullingInfo }) as any,
      ).toBlob();
      triggerDownload(blob, filename);
    } finally {
      setIsGenerating(false);
    }
  };

  return {
    downloadProposalPdf,
    downloadTallyPdf,
    downloadPullingPdf,
    isGenerating,
  };
};
